// @ts-nocheck
import React, { useState, useEffect } from 'react'
import { Modal, Form, Input, Button, message, Spin, InputNumber, Space, Table } from 'antd'
import MaterialIcon from './MaterialIcon'
import { gmailService } from '../services/gmailService'
import PriceBookSelector from './PriceBookSelector'

interface CreateEstimateModalProps {
  visible: boolean
  threadId: string
  customerId: string
  customerName: string
  customerData?: {
    address?: string
    city?: string
    state?: string
    zipCode?: string
  }
  senderEmail?: string
  onClose: () => void
  onSuccess: (estimate: any) => void
}

const CreateEstimateModal: React.FC<CreateEstimateModalProps> = ({
  visible,
  threadId,
  customerId,
  customerName,
  customerData,
  senderEmail,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm()
  const [extracting, setExtracting] = useState(false)
  const [creating, setCreating] = useState(false)
  const [items, setItems] = useState<any[]>([])
  const [showPriceBook, setShowPriceBook] = useState(false)

  useEffect(() => {
    if (visible && threadId) {
      extractEstimateData()
    }
  }, [visible, threadId, senderEmail, customerData])

  const extractEstimateData = async () => {
    try {
      setExtracting(true)
      
      // Get thread data to parse price book items from emails
      const threadResponse = await gmailService.getThread(threadId, senderEmail)
      
      // Parse price book items from email HTML content
      const parsedItems = parseItemsFromEmailThread(threadResponse.messages)
      
      // Get AI extraction
      const response = await gmailService.extractEstimate(threadId, senderEmail)
      
      console.log('🤖 AI Extraction Response:', response)
      
      if (response.success) {
        const { items: extractedItems, ...restData } = response.data
        
        console.log('📝 AI Extracted Data:', restData)
        console.log('🛠️ AI Extracted Items:', extractedItems)
        
        // Build service address from customer data
        const serviceAddress = customerData 
          ? [
              customerData.address,
              customerData.city,
              customerData.state,
              customerData.zipCode
            ].filter(Boolean).join(', ')
          : restData.serviceAddress || ''
        
        const formValues = {
          ...restData,
          serviceAddress,
        }
        
        console.log('📋 Setting form values:', formValues)
        
        form.setFieldsValue(formValues)
        
        // Prioritize parsed items from email quotes, then AI extracted items
        if (parsedItems.length > 0) {
          setItems(parsedItems)
          message.success(`Found ${parsedItems.length} items from email quotes!`)
        } else if (extractedItems && extractedItems.length > 0) {
          setItems(extractedItems)
          message.success('Estimate data extracted successfully!')
        } else {
          setItems([{ description: '', quantity: 1, unitPrice: 0 }])
          message.info('No items found, please add manually')
        }
      }
    } catch (error: any) {
      console.error('Extract estimate error:', error)
      
      // Still try to set service address from customer data
      if (customerData) {
        const serviceAddress = [
          customerData.address,
          customerData.city,
          customerData.state,
          customerData.zipCode
        ].filter(Boolean).join(', ')
        
        form.setFieldsValue({ serviceAddress })
      }
      
      message.error(error.response?.data?.message || 'Failed to extract estimate data')
      setItems([{ description: '', quantity: 1, unitPrice: 0 }])
    } finally {
      setExtracting(false)
    }
  }

  // Parse price book items from email HTML content
  const parseItemsFromEmailThread = (messages: any[]): any[] => {
    const items: any[] = []
    
    console.log('🔍 Parsing items from', messages.length, 'messages')
    
    messages.forEach((msg, idx) => {
      const body = msg.body || ''
      console.log(`📧 Message ${idx + 1} body length:`, body.length)
      
      // First, try to extract metadata (most reliable method)
      const metadataMatch = body.match(/<!--\s*QUOTE_METADATA:([A-Za-z0-9+/=]+)\s*-->/i)
      
      if (metadataMatch) {
        try {
          const decodedMetadata = atob(metadataMatch[1])
          const metadata = JSON.parse(decodedMetadata)
          
          console.log('✨ Found embedded metadata!', metadata)
          
          if (metadata.items && Array.isArray(metadata.items)) {
            metadata.items.forEach((item: any) => {
              console.log('📦 Processing metadata item:', JSON.stringify(item, null, 2))
              
              // Get the best description (try name first, then description, then fallback)
              const description = item.name || item.description || item.id || 'Unknown Item'
              
              // Check if item not already added
              if (!items.find(i => i.description === description || i.id === item.id)) {
                items.push({
                  id: item.id,
                  description,
                  quantity: item.quantity || 1,
                  unitPrice: item.unitPrice || 0,
                })
                console.log('✅ Extracted item from metadata:', description, `(ID: ${item.id})`)
              }
            })
          }
          
          // If we found metadata, skip HTML parsing for this message
          return
        } catch (e) {
          console.warn('Failed to parse metadata:', e)
        }
      }
      
      // Log a sample of the HTML for debugging (only if no metadata found)
      if (body.includes('Price Quote') || body.includes('Recommended Products')) {
        console.log(`📝 Sample HTML from message ${idx + 1} (no metadata found):`)
        console.log(body.substring(0, 2000)) // First 2000 chars
      }
      
      // Pattern 1: New card-based format with flexbox
      // Looks for: <strong>1. Item Name</strong> followed by Qty: X × $XX.XX and subtotal
      const cardPattern = /<strong[^>]*style="[^"]*color:\s*#(?:1890ff|faad14)[^"]*"[^>]*>(?:⭐\s*)?(\d+)\.\s*([^<]+)<\/strong>[\s\S]*?Qty:\s*(\d+)\s*×\s*\$(\d+(?:\.\d{2})?)/gi
      
      let match
      while ((match = cardPattern.exec(body)) !== null) {
        const name = match[2].trim()
        const quantity = parseInt(match[3])
        const unitPrice = parseFloat(match[4])
        
        console.log('✅ Found item (card format):', { name, quantity, unitPrice })
        
        if (!items.find(item => item.description === name)) {
          items.push({
            description: name,
            quantity,
            unitPrice,
          })
        }
      }
      
          // Pattern 2: Look for simple format without strong tags
          const simplePattern = /(\d+)\.\s*([^\n<]+)[\s\S]*?Qty:\s*(\d+)\s*×\s*\$(\d+(?:\.\d{2})?)/gi
          
          while ((match = simplePattern.exec(body)) !== null) {
            const name = match[2].trim()
            const quantity = parseInt(match[3])
            const unitPrice = parseFloat(match[4])
            
            console.log('✅ Found item (simple format):', { name, quantity, unitPrice })
            
            // Skip invalid items (too short, just numbers, etc.)
            if (name.length < 3 || /^\d+$/.test(name)) {
              console.log('⚠️ Skipping invalid item:', name)
              continue
            }
            
            if (!items.find(item => item.description === name)) {
              items.push({
                description: name,
                quantity,
                unitPrice,
              })
            }
          }
      
      // Pattern 3: Table format (old style)
      const tablePattern = /<td[^>]*>\s*(\d+)\s*<\/td>\s*<td[^>]*>\s*<strong>([^<]+)<\/strong>[\s\S]*?<\/td>\s*<td[^>]*>\s*(\d+)\s*<\/td>\s*<td[^>]*>\s*\$(\d+(?:\.\d{2})?)\s*<\/td>/gi
      
      while ((match = tablePattern.exec(body)) !== null) {
        const name = match[2].trim()
        const quantity = parseInt(match[3])
        const unitPrice = parseFloat(match[4])
        
        console.log('✅ Found item (table format):', { name, quantity, unitPrice })
        
        if (!items.find(item => item.description === name)) {
          items.push({
            description: name,
            quantity,
            unitPrice,
          })
        }
      }

      // Pattern 4: Old email format - Number + <strong>Name</strong> ... Quantity Price
      // Example: 1<strong>Item Name</strong></p><p>...description...</span>1$2500.00<strong>$2500.00</strong>
      const oldPattern = /(\d+)<strong>([^<]+)<\/strong>[\s\S]*?<\/span>(\d+)\$(\d+(?:\.\d{2})?)<strong>\$\d+/gi
      
      while ((match = oldPattern.exec(body)) !== null) {
        const name = match[2].trim()
        const quantity = parseInt(match[3])
        const unitPrice = parseFloat(match[4])
        
        console.log('✅ Found item (old format):', { name, quantity, unitPrice })
        
        // Skip invalid items
        if (name.length < 3 || /^\d+$/.test(name)) {
          console.log('⚠️ Skipping invalid item:', name)
          continue
        }
        
        // Decode HTML entities
        const decodedName = name
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&quot;/g, '"')
        
        if (!items.find(item => item.description === decodedName)) {
          items.push({
            description: decodedName,
            quantity,
            unitPrice,
          })
        }
      }
    })
    
    console.log('📦 Total items parsed:', items.length)
    
    return items
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      setCreating(true)
      
      const estimateData = {
        ...values,
        items: items.filter(item => item.description), // Only include items with descriptions
      }
      
      const response = await gmailService.createEstimate(threadId, customerId, estimateData)
      
      if (response.success) {
        message.success('Estimate created successfully!')
        onSuccess(response.data)
        form.resetFields()
        setItems([])
        onClose()
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to create estimate')
    } finally {
      setCreating(false)
    }
  }

  const addItem = () => {
    setItems([...items, { description: '', quantity: 1, unitPrice: 0 }])
  }

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index))
  }

  const updateItem = (index: number, field: string, value: any) => {
    const newItems = [...items]
    newItems[index][field] = value
    setItems(newItems)
  }

  const handleAddPriceBookItems = (priceBookItems: any[]) => {
    const newItems = priceBookItems.map(item => ({
      id: item.id,
      description: item.name,
      quantity: item.quantity,
      unitPrice: item.unitPrice,
    }))
    
    setItems([...items, ...newItems])
    message.success(`Added ${priceBookItems.length} items from price book!`)
  }

  const columns = [
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text: string, record: any, index: number) => (
        <Input
          value={text}
          onChange={(e) => updateItem(index, 'description', e.target.value)}
          placeholder="Service or item description"
        />
      ),
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      render: (text: number, record: any, index: number) => (
        <InputNumber
          value={text}
          onChange={(value) => updateItem(index, 'quantity', value || 1)}
          min={1}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Unit Price',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 120,
      render: (text: number, record: any, index: number) => (
        <InputNumber
          value={text}
          onChange={(value) => updateItem(index, 'unitPrice', value || 0)}
          min={0}
          prefix="$"
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Total',
      key: 'total',
      width: 120,
      render: (text: any, record: any) => `$${((record.quantity || 0) * (record.unitPrice || 0)).toFixed(2)}`,
    },
    {
      title: '',
      key: 'action',
      width: 50,
      render: (text: any, record: any, index: number) => (
        <Button
          type="text"
          danger
          icon={<MaterialIcon icon="delete" />}
          onClick={() => removeItem(index)}
          disabled={items.length === 1}
        />
      ),
    },
  ]

  const subtotal = items.reduce((sum, item) => sum + (item.quantity || 0) * (item.unitPrice || 0), 0)

  return (
    <Modal
      title={
        <span>
          <MaterialIcon icon="description" style={{ marginRight: '8px' }} />
          Create Estimate for {customerName}
        </span>
      }
      open={visible}
      onCancel={onClose}
      width={900}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="extract"
          icon={<MaterialIcon icon="auto_awesome" />}
          onClick={extractEstimateData}
          loading={extracting}
          disabled={creating}
        >
          Re-extract
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={creating}
          disabled={extracting}
        >
          Create Estimate
        </Button>,
      ]}
    >
      {extracting ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>
            <MaterialIcon icon="auto_awesome" style={{ fontSize: '24px' }} />
            <div>AI is extracting estimate data from email...</div>
          </div>
        </div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          disabled={creating}
        >
          <Form.Item
            label="Service Type"
            name="serviceType"
            rules={[{ required: true, message: 'Please enter service type' }]}
          >
            <Input prefix={<MaterialIcon icon="build" />} placeholder="e.g., Garage Door Repair" />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
          >
            <Input.TextArea rows={2} placeholder="Brief description of work to be done" />
          </Form.Item>

          <Form.Item
            label="Service Address"
            name="serviceAddress"
          >
            <Input prefix={<MaterialIcon icon="location_on" />} placeholder="Where will the work be performed?" />
          </Form.Item>

          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <strong>Items / Services</strong>
              <Space>
                <Button
                  type="default"
                  icon={<MaterialIcon icon="storefront" />}
                  onClick={() => setShowPriceBook(true)}
                >
                  Add from Price Book
                </Button>
                <Button
                  type="dashed"
                  icon={<MaterialIcon icon="add" />}
                  onClick={addItem}
                >
                  Add Item
                </Button>
              </Space>
            </div>
            <Table
              dataSource={items}
              columns={columns}
              pagination={false}
              size="small"
              rowKey={(record, index) => index || 0}
            />
            <div style={{ marginTop: '8px', textAlign: 'right' }}>
              <strong>Subtotal: ${subtotal.toFixed(2)}</strong>
            </div>
          </div>

          <Form.Item
            label="Notes"
            name="notes"
          >
            <Input.TextArea rows={2} placeholder="Any special requirements or notes" />
          </Form.Item>
        </Form>
      )}
      
      {/* Price Book Selector Modal */}
      <PriceBookSelector
        visible={showPriceBook}
        onClose={() => setShowPriceBook(false)}
        onSelect={handleAddPriceBookItems}
        buttonText="Add to Estimate"
      />
    </Modal>
  )
}

export default CreateEstimateModal

